import React from "react";
import { useNavigate } from "react-router-dom";
import { FiCheck, FiX } from "react-icons/fi";

// Type-safe icon wrappers
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

interface WorkflowCreatedModalProps {
  isOpen: boolean;
  workflowName: string;
  subject: string;
  recipientCount: number;
  frequency: string;
  firstSendDate: string;
  onClose: () => void;
}

/**
 * WorkflowCreatedModal - Success confirmation after scheduling a workflow
 */
const WorkflowCreatedModal: React.FC<WorkflowCreatedModalProps> = ({
  isOpen,
  workflowName,
  subject,
  recipientCount,
  frequency,
  firstSendDate,
  onClose,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleComposeNew = () => {
    // Clear compose state before navigating
    localStorage.removeItem('compose_temp_state');
    // Navigate to compose page with fresh state
    navigate("/updates/new", { replace: true });
  };

  const handleViewWorkflows = () => {
    // Clear compose state before navigating
    localStorage.removeItem('compose_temp_state');
    navigate("/email-updates?tab=workflows");
  };

  const handleClose = () => {
    // Clear compose state before closing
    localStorage.removeItem('compose_temp_state');
    navigate("/email-updates?tab=workflows");
    onClose();
  };

  // Format frequency for subtitle
  const frequencyLower = frequency.toLowerCase();

  return (
    <div className="su-modal-overlay">
      <div className="su-modal su-workflow-created-modal">
        {/* Close Button */}
        <button
          className="su-workflow-created-close"
          onClick={handleClose}
          aria-label="Close"
        >
          <CloseIcon className="w-5 h-5" />
        </button>

        {/* Success Icon */}
        <div className="su-workflow-created-icon">
          <CheckIcon className="w-10 h-10" />
        </div>

        {/* Title */}
        <h2 className="su-workflow-created-title">Workflow Created!</h2>
        <p className="su-workflow-created-subtitle">
          Your {frequencyLower} workflow is now active
        </p>

        {/* Summary Card */}
        <div className="su-section su-workflow-created-summary">
          <div className="su-workflow-created-summary-header">
            WORKFLOW SUMMARY
          </div>

          <div className="su-workflow-created-rows">
            <div className="su-workflow-created-row">
              <span className="su-workflow-created-label">Workflow Name</span>
              <span className="su-workflow-created-value">{workflowName}</span>
            </div>

            <div className="su-workflow-created-row">
              <span className="su-workflow-created-label">Subject</span>
              <span className="su-workflow-created-value">{subject || "Not set"}</span>
            </div>

            <div className="su-workflow-created-row">
              <span className="su-workflow-created-label">Recipients</span>
              <span className="su-workflow-created-value">{recipientCount}</span>
            </div>

            <div className="su-workflow-created-row">
              <span className="su-workflow-created-label">Frequency</span>
              <span className="su-workflow-created-value">{frequency}</span>
            </div>

            <div className="su-workflow-created-row">
              <span className="su-workflow-created-label">First Send</span>
              <span className="su-workflow-created-value">{firstSendDate}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="su-workflow-created-actions">
          <button
            className="su-btn-primary su-workflow-created-btn"
            onClick={handleComposeNew}
          >
            COMPOSE NEW UPDATE
          </button>
          <button
            className="su-btn-secondary su-workflow-created-btn"
            onClick={handleViewWorkflows}
          >
            VIEW WORKFLOWS
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowCreatedModal;


